import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import PlatformCard from '@/components/base/PlatformCard';
import { useSeo } from '@/hooks/useSeo';
import { platforms } from '@/mocks/platforms';
import { categories } from '@/mocks/categories';

const SITE_URL = 'https://luosai123.cn';

const sortOptions = [
  { value: 'heat', label: '热度优先' },
  { value: 'rating', label: '评分优先' },
  { value: 'updated', label: '最近核验' },
];

export default function DirectoryPage() {
  const [searchParams] = useSearchParams();
  const paramCategory = searchParams.get('category') || 'all';
  const paramQuery = searchParams.get('q') || '';

  const [category, setCategory] = useState(paramCategory);
  const [query, setQuery] = useState(paramQuery);
  const [sort, setSort] = useState('heat');

  useEffect(() => {
    setCategory(paramCategory);
  }, [paramCategory]);

  useEffect(() => {
    setQuery(paramQuery);
  }, [paramQuery]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const list = platforms.filter((item) => {
      const matchCategory = category === 'all' || item.categoryId === category;
      if (!matchCategory) {
        return false;
      }
      if (!keyword) {
        return true;
      }
      return (
        item.name.toLowerCase().includes(keyword) ||
        item.description.toLowerCase().includes(keyword) ||
        item.categoryName.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword))
      );
    });

    return [...list].sort((a, b) => {
      if (sort === 'rating') {
        return b.rating - a.rating;
      }
      if (sort === 'updated') {
        return b.updatedAt.localeCompare(a.updatedAt);
      }
      return b.heat - a.heat;
    });
  }, [category, query, sort]);

  const activeCategoryName =
    category === 'all' ? '全部平台' : categories.find((item) => item.id === category)?.name || '全部平台';

  const jsonLd = useMemo(() => {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '平台导航大全',
        url: `${SITE_URL}/directory`,
        description: '按类别收录的爆料平台与发布入口导航，支持关键词搜索与热度排序。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '爆料平台导航列表',
        numberOfItems: platforms.length,
        itemListElement: platforms.slice(0, 20).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          url: item.url,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '平台导航大全', item: `${SITE_URL}/directory` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: '平台导航大全 - 爆料网站与发布入口汇总｜爆料网上',
    description:
      '爆料网上平台导航大全收录 186 个爆料网站与发布入口，按综合、消费维权、财经、城市本地等类别整理，支持关键词搜索、热度与评分排序，快速找到匹配渠道。',
    keywords: '爆料网站大全,爆料平台导航,爆料入口,发布入口汇总,爆料网上',
    path: '/directory',
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '平台导航大全' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            平台导航大全
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            按类别收录的爆料网站与发布入口。每个条目都标注了最近一次核验日期与材料要求，
            选准类别再挑入口，比到处盲目投递有效得多。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索平台名称、类别或关键词"
                aria-label="搜索平台入口"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="whitespace-nowrap text-sm text-foreground-600">
                排序
              </label>
              <select
                id="sort-select"
                value={sort}
                onChange={(event) => setSort(event.target.value)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 cursor-pointer"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                category === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              全部平台
            </button>
            {categories.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setCategory(item.id)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                  category === item.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={item.icon}></i>
                {item.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-baseline justify-between">
          <p className="text-sm text-foreground-600">
            当前：<span className="font-medium text-foreground-950">{activeCategoryName}</span>
            <span className="ml-2 text-foreground-400">共 {filtered.length} 个条目</span>
          </p>
          {(query || category !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setCategory('all');
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((platform) => (
              <PlatformCard key={platform.id} platform={platform} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有匹配的入口</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
              换个关键词试试，或者先浏览全部类别。如果确实缺少你需要的渠道，欢迎通过联系我们页面反馈。
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setCategory('all');
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              查看全部平台
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}